import { useEffect, useRef, useState } from 'react'
import { usePage } from '@inertiajs/react'
import { Download, Loader2, Trash2 } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import { Separator } from '@/shared/ui/separator'
import { Button } from '@/shared/ui/button'
import { CommentBody } from '@/features/comments/CommentsPanel'
import { t } from '@/shared/lib/i18n'
import apiClient from '@/shared/lib/apiClient'
import {
  instrumentReportsServiceReportsByDatePath,
  instrumentServiceReportCommentsPath,
  instrumentServiceReportCommentPath,
} from '@/shared/lib/routes'
import type { SharedProps } from '@/shared/types/SharedProps'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { useSiteDateTime } from '@/shared/hooks/useSiteDateTime'

interface ServiceReportComment {
  id: number
  body: string
  authorName: string
  userId: number | null
  createdAt: string
}

interface ServiceReportAttachment {
  id: number
  attachmentName: string | null
  downloadHref: string
}

interface ServiceReport {
  id: number
  completedAt: string
  completedByName: string | null
  problemDescription: string | null
  attachments: ServiceReportAttachment[]
  comments: ServiceReportComment[]
}

interface Props {
  instrumentId: number
  date: string | null
  onClose: () => void
}

type LoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'loaded'; records: ServiceReport[] }

const InstrumentServiceReportListDialog = ({ instrumentId, date, onClose }: Props) => {
  const { formatDateTime } = useSiteDateTime()
  const { currentUserInfo } = usePage<SharedProps>().props
  const canDeleteComment = useCan(Resources.COMMENTS, Actions.DELETE)
  const canCreateComment = useCan(Resources.COMMENTS, Actions.CREATE)

  const [loadState, setLoadState] = useState<LoadState>({ status: 'idle' })
  const [commentDraft, setCommentDraft] = useState<Record<number, string>>({})
  const [submittingForReport, setSubmittingForReport] = useState<number | null>(null)
  const [deletingCommentId, setDeletingCommentId] = useState<number | null>(null)

  const textareaRefs = useRef<Record<number, HTMLTextAreaElement | null>>({})

  const fetchRecords = (targetDate: string) => {
    setLoadState({ status: 'loading' })
    apiClient
      .get<{ serviceReports: ServiceReport[] }>(
        instrumentReportsServiceReportsByDatePath(instrumentId, { date: targetDate }),
      )
      .then((res) => setLoadState({ status: 'loaded', records: res.data.serviceReports }))
      .catch(() => setLoadState({ status: 'error' }))
  }

  useEffect(() => {
    if (date) fetchRecords(date)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instrumentId, date])

  const updateCommentsForReport = (reportId: number, updatedComments: ServiceReportComment[]) => {
    setLoadState((prev) => {
      if (prev.status !== 'loaded') return prev
      return {
        ...prev,
        records: prev.records.map((r) =>
          r.id === reportId ? { ...r, comments: updatedComments } : r,
        ),
      }
    })
  }

  const handleSubmitComment = async (reportId: number) => {
    const body = (commentDraft[reportId] ?? '').trim()
    if (!body) return

    setSubmittingForReport(reportId)
    try {
      const res = await apiClient.post<{ comments: ServiceReportComment[] }>(
        instrumentServiceReportCommentsPath(instrumentId, reportId),
        { body },
      )
      updateCommentsForReport(reportId, res.data.comments)
      setCommentDraft((prev) => ({ ...prev, [reportId]: '' }))
    } finally {
      setSubmittingForReport(null)
    }
  }

  const handleDeleteComment = async (reportId: number, commentId: number) => {
    setDeletingCommentId(commentId)
    try {
      const res = await apiClient.delete<{ comments: ServiceReportComment[] }>(
        instrumentServiceReportCommentPath(instrumentId, reportId, commentId),
      )
      updateCommentsForReport(reportId, res.data.comments)
    } finally {
      setDeletingCommentId(null)
    }
  }

  const canDeleteSpecificComment = (comment: ServiceReportComment) =>
    canDeleteComment || comment.userId === currentUserInfo?.id

  const isOpen = date != null

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[80vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('instruments.reports.serviceReports.dialogTitle')}</DialogTitle>
          {date && <p className="text-sm text-muted-foreground">{date}</p>}
        </DialogHeader>

        <Separator />

        {loadState.status === 'loading' && (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {loadState.status === 'error' && (
          <p className="py-4 text-center text-sm text-destructive">
            {t('instruments.reports.logDetails.error')}
          </p>
        )}

        {loadState.status === 'loaded' && loadState.records.length === 0 && (
          <p className="py-4 text-center text-sm text-muted-foreground">
            {t('instruments.reports.dialogs.empty')}
          </p>
        )}

        {loadState.status === 'loaded' && loadState.records.length > 0 && (
          <div className="min-w-0 space-y-4">
            {loadState.records.map((report) => (
              <div key={report.id} className="rounded border text-sm">
                <div className="space-y-1 p-3">
                  <p className="font-medium text-muted-foreground">
                    {formatDateTime(report.completedAt)}
                  </p>
                  {report.completedByName && (
                    <p className="text-xs text-muted-foreground">
                      <span className="font-medium">
                        {t('instruments.reports.serviceReports.completedBy')}
                      </span>{' '}
                      {report.completedByName}
                    </p>
                  )}
                  {report.problemDescription && (
                    <p className="text-xs whitespace-pre-wrap break-words">
                      {report.problemDescription}
                    </p>
                  )}
                  {report.attachments.length > 0 && (
                    <ul className="space-y-1 pt-1">
                      {report.attachments.map((attachment) => (
                        <li key={attachment.id}>
                          <a
                            href={attachment.downloadHref}
                            download
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-primary underline-offset-2 hover:underline"
                          >
                            <Download className="h-3.5 w-3.5" />
                            {attachment.attachmentName}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <Separator />

                <div className="space-y-2 p-3">
                  <p className="text-xs font-medium text-muted-foreground">
                    {t('instruments.reports.serviceReports.comments')}
                  </p>

                  {report.comments.length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      {t('instruments.reports.serviceReports.noComments')}
                    </p>
                  )}

                  {report.comments.map((comment) => (
                    <div key={comment.id} className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium">{comment.authorName}</p>
                        <CommentBody body={comment.body} className="text-xs" />
                      </div>
                      {canDeleteSpecificComment(comment) && (
                        <button
                          type="button"
                          disabled={deletingCommentId === comment.id}
                          onClick={() => handleDeleteComment(report.id, comment.id)}
                          className="shrink-0 text-muted-foreground hover:text-destructive disabled:opacity-50"
                          title={t('instruments.reports.serviceReports.deleteComment')}
                        >
                          {deletingCommentId === comment.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5" />
                          )}
                        </button>
                      )}
                    </div>
                  ))}

                  {canCreateComment && (
                    <div className="flex items-end gap-2 pt-1">
                      <textarea
                        ref={(el) => {
                          textareaRefs.current[report.id] = el
                        }}
                        rows={2}
                        placeholder={t('instruments.reports.serviceReports.commentPlaceholder')}
                        value={commentDraft[report.id] ?? ''}
                        onChange={(e) =>
                          setCommentDraft((prev) => ({ ...prev, [report.id]: e.target.value }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                            e.preventDefault()
                            handleSubmitComment(report.id)
                          }
                        }}
                        className="flex-1 resize-none rounded border bg-background px-2 py-1.5 text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                      />
                      <Button
                        size="sm"
                        disabled={
                          !commentDraft[report.id]?.trim() || submittingForReport === report.id
                        }
                        onClick={() => handleSubmitComment(report.id)}
                      >
                        {submittingForReport === report.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          t('instruments.reports.serviceReports.submitComment')
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default InstrumentServiceReportListDialog
